/**
 * As notificações deste aparelho — a versão em TypeScript do
 * `web/push_bridge.js` e do `push_status.dart` que o Flutter usa.
 *
 * O aviso chega sem conteúdo de propósito (ver `server/src/webpush.js`): os
 * serviços do Google e da Mozilla só ficam sabendo que *algo* aconteceu, nunca
 * o quê. O texto é buscado pelo próprio worker, na nossa API, quando acorda.
 */

import { ErroAPI } from '@nosso-sempre/shared';
import { api } from './api';

const ENDERECO_SW = '/push-sw.js';
const ESCOPO_SW = '/';

/**
 * O `PushStatus` do Flutter: o retrato deste aparelho num instante.
 *
 * `motivo` é o que deu errado, quando deu — é dele que sai quase toda a
 * explicação escrita na tela.
 */
export interface EstadoDeAviso {
  suporta: boolean;
  /** `'default' | 'granted' | 'denied' | 'unsupported'`, como o navegador diz. */
  permissao: string;
  assinado: boolean;
  aparelhos: number;
  servidorPronto: boolean;
  motivo?: string;
}

export const semSuporte = (motivo?: string): EstadoDeAviso => ({
  suporta: false,
  permissao: 'unsupported',
  assinado: false,
  aparelhos: 0,
  servidorPronto: false,
  motivo,
});

/** Bloqueado nas configurações do navegador: não adianta pedir de novo. */
export const bloqueado = (e: EstadoDeAviso): boolean => e.permissao === 'denied';

/** Ligado de verdade: o navegador deixou, este aparelho assinou e há servidor. */
export const ligado = (e: EstadoDeAviso): boolean =>
  e.assinado && e.servidorPronto && e.permissao === 'granted';

/**
 * O que dizer para a pessoa. `null` quando está tudo certo — a tela não
 * escreve nada nesse caso, que é o `explanation` nulo de lá.
 */
export function explicacao(e: EstadoDeAviso): string | null {
  if (!e.suporta) {
    return 'Este navegador não trabalha com notificações. Tente pelo Chrome ou pelo Safari 16.4 ou mais novo.';
  }
  if (bloqueado(e)) {
    return 'As notificações foram bloqueadas nas configurações do navegador. Libere-as por lá para poder ligar aqui.';
  }
  switch (e.motivo) {
    case 'session':
      return 'Entre de novo com sua senha para ligar as notificações neste aparelho.';
    case 'offline':
      return 'Sem conexão com o servidor agora.';
    case 'server_off':
      return 'O servidor ainda não está pronto para enviar notificações.';
    case 'server':
      return 'Não deu certo falar com o servidor. Tente de novo.';
    case 'default':
      return 'Você fechou o pedido de permissão. Toque de novo para permitir.';
    case undefined:
    case '':
      break;
    default:
      return 'Não foi possível ligar as notificações neste aparelho.';
  }
  if (e.assinado && !e.servidorPronto) {
    return 'Este aparelho está pronto, mas o servidor ainda não envia notificações.';
  }
  return null;
}

/** O navegador sabe receber notificações? */
export function temSuporte(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/** A inscrição já existente deste navegador, se houver. */
async function inscricaoAtual(): Promise<PushSubscription | null> {
  const registro = await navigator.serviceWorker.getRegistration(ESCOPO_SW);
  if (!registro) return null;
  return registro.pushManager.getSubscription();
}

/** Monta o retrato, perguntando ao navegador e depois ao servidor. */
async function retrato(extra: Partial<EstadoDeAviso> = {}): Promise<EstadoDeAviso> {
  if (!temSuporte()) return semSuporte(extra.motivo);

  const base: EstadoDeAviso = {
    suporta: true,
    permissao: Notification.permission,
    assinado: Boolean(await inscricaoAtual().catch(() => null)),
    aparelhos: 0,
    servidorPronto: false,
    ...extra,
  };

  try {
    const { enabled, devices } = await api.avisosLigados();
    return { ...base, servidorPronto: enabled, aparelhos: devices };
  } catch (err) {
    // 401 é a sessão vencida, e não um defeito das notificações; qualquer
    // outra falha aqui é o servidor fora de alcance.
    return { ...base, motivo: base.motivo ?? (naSessao(err) ? 'session' : 'offline') };
  }
}

const naSessao = (err: unknown): boolean => err instanceof ErroAPI && err.status === 401;

export const estado = (): Promise<EstadoDeAviso> => retrato();

/** As duas chaves são a mesma? O par VAPID pode ter sido trocado no servidor. */
function mesmaChave(inscricao: PushSubscription, chave: string): boolean {
  const atual = inscricao.options?.applicationServerKey;
  if (!atual) return false;
  const bytes = new Uint8Array(atual);
  let bruto = '';
  for (const b of bytes) bruto += String.fromCharCode(b);
  const b64 = btoa(bruto).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return b64 === chave.replace(/=+$/, '');
}

/**
 * Liga as notificações neste aparelho.
 *
 * A ordem importa: primeiro a permissão, depois a chave do servidor, e só
 * então a inscrição — pedir uma inscrição sem servidor deixaria o navegador
 * com uma assinatura que ninguém usa.
 */
export async function assinar(): Promise<EstadoDeAviso> {
  if (!temSuporte()) return semSuporte('unsupported');

  if (Notification.permission === 'default') {
    try {
      await Notification.requestPermission();
    } catch {
      /* o navegador já responde pela permissão guardada */
    }
  }
  if (Notification.permission !== 'granted') {
    return retrato({ motivo: Notification.permission });
  }

  let chave: string;
  try {
    chave = await api.chaveDeAviso();
  } catch (err) {
    const semPar = err instanceof ErroAPI && err.status === 503;
    return retrato({ motivo: semPar ? 'server_off' : 'server' });
  }
  if (!chave) return retrato({ motivo: 'server_off' });

  let inscricao: PushSubscription;
  try {
    const registro = await navigator.serviceWorker.register(ENDERECO_SW, { scope: ESCOPO_SW });
    await navigator.serviceWorker.ready;
    const antiga = await registro.pushManager.getSubscription();
    // Uma inscrição feita com a chave velha nunca receberia nada: sai antes de
    // pedir a nova, senão o navegador recusa por conflito.
    if (antiga && !mesmaChave(antiga, chave)) await antiga.unsubscribe();
    inscricao =
      antiga && mesmaChave(antiga, chave)
        ? antiga
        : await registro.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: chave,
          });
  } catch {
    return retrato({ motivo: 'server' });
  }

  try {
    await api.assinarAvisos(inscricao.endpoint);
  } catch (err) {
    return retrato({ motivo: naSessao(err) ? 'session' : 'server' });
  }
  return retrato();
}

/**
 * Desliga as notificações neste aparelho.
 *
 * O servidor esquece primeiro: se a chamada falhar, sobra uma inscrição viva
 * em vez de uma inscrição que ninguém mais alcança.
 */
export async function desassinar(): Promise<EstadoDeAviso> {
  if (!temSuporte()) return semSuporte('unsupported');

  const inscricao = await inscricaoAtual().catch(() => null);
  if (!inscricao) return retrato();

  try {
    await api.desassinarAvisos(inscricao.endpoint);
  } catch (err) {
    return retrato({ motivo: naSessao(err) ? 'session' : 'server' });
  }
  await inscricao.unsubscribe().catch(() => false);
  return retrato();
}

/** Manda um aviso de teste para os aparelhos de quem pediu. */
export const testar = (): Promise<boolean> => api.avisoDeTeste();

/**
 * Ouve o worker: clicar num aviso não recarrega o app, avisa a rota.
 *
 * O worker fala em rota do Flutter (`/dashboard`, `/gallery`); o site usa
 * `HashRouter` e outros nomes, então a tradução é aqui, onde o roteador está.
 */
const ROTAS: Record<string, string> = {
  '/dashboard': '/',
  '/gallery': '/galeria',
  // O `/starlight` do Flutter é o pote de estrelas, que aqui se chama
  // Lembranças. Antes isto caía no painel, que só mostra a prévia dele: um
  // aviso de estrela nova abre o pote, como no Flutter.
  '/starlight': '/lembrancas',
  '/sanctuary': '/sanctuary',
  '/cinema': '/cinema',
  '/kovu': '/kovu',
};

export function ouvirCliques(navegar: (rota: string) => void): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};

  const aoReceber = (evento: MessageEvent) => {
    const dado = evento.data as { source?: string; route?: string } | null;
    if (!dado || dado.source !== 'nosso-sempre-push') return;
    const rota = dado.route ?? '/dashboard';
    navegar(ROTAS[rota] ?? rota);
  };

  navigator.serviceWorker.addEventListener('message', aoReceber);
  return () => navigator.serviceWorker.removeEventListener('message', aoReceber);
}
